import { Injectable, computed, signal } from '@angular/core';
import {
  WorkflowEdgeRequest,
  WorkflowGraphResponse,
  WorkflowGraphSaveRequest,
  WorkflowNodeRequest,
  WorkflowNodeResponse,
  WorkflowEdgeResponse,
  WorkflowNodeType,
  WorkflowValidationErrorResponse,
  WorkflowValidationResponse
} from '../../models/workflow-builder.model';
import {
  WorkflowGraphEdge,
  WorkflowGraphNode,
  WorkflowGraphSelection,
  WorkflowGraphSnapshot,
  WorkflowSaveState,
  buildEdgeId
} from '../models/flow-graph.model';
import { getOutputBranchHandles } from '../models/node-catalog';
import { generateNodeKey, parseNodeConfig } from '../models/node-config.util';

const MAX_HISTORY = 50;
const DEFAULT_BRANCH_KEY = 'DEFAULT';
/**
 * Ventana para agrupar ediciones seguidas del MISMO campo en un solo paso de deshacer.
 * Los campos de texto del inspector confirman en cada pulsación (evento `input`), así que sin
 * esto una frase escrita llenaría la pila de deshacer y clonaría el grafo letra a letra.
 */
const HISTORY_COALESCE_MS = 800;

/**
 * Única fuente de verdad del grafo del nuevo builder de workflow.
 *
 * Se calca de `ChatflowGraphStore` (signals: nodes, edges, selection, dirty, undo/redo) y
 * se adapta a workflow:
 *
 * - Los NODOS conservan su `id` de BD (no rota: `syncNodes` lo preserva por id/nodeKey).
 * - Las ARISTAS se identifican por `nodeKey` + `branchKey` (el PUERTO). Su id de BD rota
 *   en cada `saveGraph` (deleteByWorkflowVersion + recrea), así que aquí no se persiste.
 * - `saveGraph` es REPLACE: `toSaveRequest()` debe emitir la verdad COMPLETA del grafo
 *   (todos los nodos y todas las aristas resolubles), o el backend borra lo que no viaje.
 *
 * No se provee en root: el dueño es el shell del builder, de modo que cada workflow
 * abierto tenga su propio estado.
 */
@Injectable()
export class WorkflowGraphStore {
  private readonly _versionId = signal<number | null>(null);
  private readonly _nodes = signal<WorkflowGraphNode[]>([]);
  private readonly _edges = signal<WorkflowGraphEdge[]>([]);
  private readonly _validation = signal<WorkflowValidationResponse | null>(null);
  private readonly _selection = signal<WorkflowGraphSelection>(null);
  private readonly _dirty = signal(false);
  /**
   * Cambios que solo persiste el PUT del grafo (crear/borrar/conectar…), a diferencia de
   * mover un nodo, que se persiste con el PATCH barato de posición.
   */
  private readonly _structuralDirty = signal(false);
  private readonly _saveState = signal<WorkflowSaveState>('idle');

  private undoStack: WorkflowGraphSnapshot[] = [];
  private redoStack: WorkflowGraphSnapshot[] = [];
  private readonly _canUndo = signal(false);
  private readonly _canRedo = signal(false);

  /** Última ráfaga de edición agrupada en el historial (ver HISTORY_COALESCE_MS). */
  private lastEditToken = '';
  private lastEditAt = 0;

  /**
   * Contador de mutaciones locales. El editor lo captura antes de lanzar el PUT del grafo: si
   * cambió cuando llega la respuesta, el usuario siguió editando mientras el guardado viajaba y
   * la respuesta NO puede reemplazar el estado (se perdería lo tecleado durante ese hueco).
   */
  private _revision = 0;
  revision(): number {
    return this._revision;
  }

  readonly versionId = this._versionId.asReadonly();
  readonly nodes = this._nodes.asReadonly();
  readonly edges = this._edges.asReadonly();
  readonly validation = this._validation.asReadonly();
  readonly selection = this._selection.asReadonly();
  readonly dirty = this._dirty.asReadonly();
  readonly saveState = this._saveState.asReadonly();
  readonly canUndo = this._canUndo.asReadonly();
  readonly canRedo = this._canRedo.asReadonly();

  /** Ya existe un TRIGGER: la paleta debe deshabilitar el ítem (un workflow tiene un disparador). */
  readonly hasTriggerNode = computed(() => this._nodes().some((node) => node.nodeType === 'TRIGGER'));

  /** Errores de validación agrupados por `nodeKey` → badge "Incompleto" en la tarjeta. */
  readonly nodeErrors = computed<Map<string, WorkflowValidationErrorResponse[]>>(() => {
    const byId = new Map<number, string>();
    for (const node of this._nodes()) {
      if (node.id !== null) {
        byId.set(node.id, node.nodeKey);
      }
    }
    const map = new Map<string, WorkflowValidationErrorResponse[]>();
    for (const error of this._validation()?.errors ?? []) {
      if (error.scope !== 'NODE' || error.nodeId == null) {
        continue;
      }
      const nodeKey = byId.get(error.nodeId);
      if (!nodeKey) {
        continue;
      }
      const list = map.get(nodeKey) ?? [];
      list.push(error);
      map.set(nodeKey, list);
    }
    return map;
  });

  /** Avisos de estructura del flujo → barra inferior (fases posteriores). */
  readonly graphErrors = computed<WorkflowValidationErrorResponse[]>(() =>
    (this._validation()?.errors ?? []).filter((error) => error.scope === 'GRAPH')
  );

  // ── Carga y reconciliación ────────────────────────────────────────────────

  /**
   * Sustituye el estado con la respuesta del backend (GET o PUT de /builder/graph).
   * Reconcilia SIEMPRE contra la respuesta: los ids de arista rotan en cada guardado, así
   * que no se conserva nada del estado anterior de las aristas.
   */
  loadFromGraph(graph: WorkflowGraphResponse): void {
    this._versionId.set(graph.workflowVersionId);
    const nodes = (graph.nodes ?? []).map((node) => this.toGraphNode(node));
    const nodesById = new Map<number, WorkflowGraphNode>();
    for (const node of nodes) {
      if (node.id !== null) {
        nodesById.set(node.id, node);
      }
    }

    const edges: WorkflowGraphEdge[] = [];
    for (const edge of graph.edges ?? []) {
      const source = nodesById.get(edge.sourceNodeId);
      const target = nodesById.get(edge.targetNodeId);
      if (!source || !target) {
        // Referencia a un nodo ausente del grafo: no se puede reenviar por id en el PUT.
        continue;
      }
      edges.push(this.toGraphEdge(edge, source.nodeKey, target.nodeKey));
    }

    this._nodes.set(nodes);
    this._edges.set(edges);
    this._validation.set(graph.validation ?? null);
    this._dirty.set(false);
    this._structuralDirty.set(false);
    this.undoStack = [];
    this.redoStack = [];
    this.lastEditToken = '';
    this.syncHistoryFlags();
  }

  /**
   * Reconciliación MÍNIMA tras un PUT cuyo resultado NO debe reemplazar el estado local: el
   * usuario siguió editando mientras el guardado viajaba y `loadFromGraph` borraría esos cambios.
   *
   * Solo se adopta lo que el frontend no puede inventar —la versión editable y el id de BD de los
   * nodos que aún no lo tenían—, y el grafo se deja SUCIO para que el siguiente PUT lo persista.
   */
  adoptServerIds(graph: WorkflowGraphResponse): void {
    this._versionId.set(graph.workflowVersionId);
    const idByKey = new Map<string, number>();
    for (const node of graph.nodes ?? []) {
      if (node.id != null) {
        idByKey.set(node.nodeKey, node.id);
      }
    }
    this._nodes.update((nodes) =>
      nodes.map((node) => (node.id === null && idByKey.has(node.nodeKey) ? { ...node, id: idByKey.get(node.nodeKey)! } : node))
    );
  }

  private toGraphNode(node: WorkflowNodeResponse): WorkflowGraphNode {
    return {
      id: node.id,
      nodeKey: node.nodeKey,
      nodeType: node.nodeType,
      nodeSubtype: node.nodeSubtype ?? null,
      title: node.title ?? '',
      description: node.description ?? null,
      config: parseNodeConfig(node.config),
      positionX: this.toNumber(node.positionX),
      positionY: this.toNumber(node.positionY),
      validationStatus: node.validationStatus ?? 'PENDING',
      isActive: node.isActive
    };
  }

  private toGraphEdge(edge: WorkflowEdgeResponse, sourceNodeKey: string, targetNodeKey: string): WorkflowGraphEdge {
    return {
      sourceNodeKey,
      targetNodeKey,
      branchKey: this.normalizeBranchKey(edge.branchKey),
      label: edge.label ?? '',
      sortOrder: edge.sortOrder ?? 0,
      config: parseNodeConfig(edge.config)
    };
  }

  private normalizeBranchKey(branchKey: string | null | undefined): string {
    const trimmed = (branchKey ?? '').trim();
    return trimmed || DEFAULT_BRANCH_KEY;
  }

  private toNumber(value: number | null | undefined): number {
    return typeof value === 'number' && !Number.isNaN(value) ? Math.round(value) : 0;
  }

  // ── Mutaciones ────────────────────────────────────────────────────────────

  /** Crea un nodo en las coordenadas indicadas y lo selecciona. */
  addNode(
    nodeType: WorkflowNodeType,
    nodeSubtype: string | null,
    positionX: number,
    positionY: number,
    options: { title?: string; description?: string | null; config?: Record<string, unknown> } = {}
  ): WorkflowGraphNode {
    this.pushHistory();
    const node: WorkflowGraphNode = {
      id: null,
      nodeKey: generateNodeKey(nodeType, this._nodes().map((n) => n.nodeKey)),
      nodeType,
      nodeSubtype,
      title: options.title ?? '',
      description: options.description ?? null,
      config: options.config ? structuredClone(options.config) : {},
      positionX: Math.round(positionX),
      positionY: Math.round(positionY),
      validationStatus: 'PENDING',
      isActive: true
    };
    this._nodes.update((nodes) => [...nodes, node]);
    this._selection.set({ kind: 'node', nodeKey: node.nodeKey });
    this.markDirty();
    return node;
  }

  /**
   * Actualiza campos de un nodo. `nodeKey`, `id` y `nodeType` NO se pueden cambiar: son la
   * identidad para el backend (`syncNodes` referencia por id/nodeKey).
   */
  updateNode(
    nodeKey: string,
    patch: Partial<Omit<WorkflowGraphNode, 'nodeKey' | 'id' | 'nodeType'>>
  ): void {
    this.pushHistory(`node:${nodeKey}:${Object.keys(patch).join(',')}`);
    this._nodes.update((nodes) => nodes.map((node) => (node.nodeKey === nodeKey ? { ...node, ...patch } : node)));
    this.markDirty();
  }

  /** Reemplaza el config (ya parseado) de un nodo. */
  updateNodeConfig(nodeKey: string, config: Record<string, unknown>): void {
    this.pushHistory(`config:${nodeKey}`);
    this._nodes.update((nodes) => nodes.map((node) => (node.nodeKey === nodeKey ? { ...node, config } : node)));
    this.markDirty();
  }

  /** Mueve un nodo. `history=false` durante el arrastre para no llenar la pila. */
  moveNode(nodeKey: string, positionX: number, positionY: number, history = true): void {
    if (history) {
      this.pushHistory();
    }
    this._nodes.update((nodes) =>
      nodes.map((node) =>
        node.nodeKey === nodeKey ? { ...node, positionX: Math.round(positionX), positionY: Math.round(positionY) } : node
      )
    );
    // Mover no es estructural: lo persiste el PATCH de posición, no el PUT del grafo.
    this.markDirty(false);
  }

  /** Cuántas conexiones se llevaría por delante borrar estos nodos. */
  countConnections(nodeKeys: readonly string[]): number {
    const keys = new Set(nodeKeys);
    return this._edges().filter((e) => keys.has(e.sourceNodeKey) || keys.has(e.targetNodeKey)).length;
  }

  /** Borra nodos y todas sus conexiones entrantes/salientes. */
  removeNodes(nodeKeys: readonly string[]): void {
    if (!nodeKeys.length) {
      return;
    }
    this.pushHistory();
    const keys = new Set(nodeKeys);
    this._nodes.update((nodes) => nodes.filter((node) => !keys.has(node.nodeKey)));
    this._edges.update((edges) => edges.filter((e) => !keys.has(e.sourceNodeKey) && !keys.has(e.targetNodeKey)));
    const selection = this._selection();
    if (selection?.kind === 'node' && keys.has(selection.nodeKey)) {
      this._selection.set(null);
    }
    this.markDirty();
  }

  removeEdge(edgeId: string): void {
    this.pushHistory();
    this._edges.update((edges) => edges.filter((e) => buildEdgeId(e) !== edgeId));
    const selection = this._selection();
    if (selection?.kind === 'edge' && selection.edgeId === edgeId) {
      this._selection.set(null);
    }
    this.markDirty();
  }

  /**
   * Duplica nodos desplazados para que se vean, CON las conexiones internas del grupo.
   *
   * INC_CRM_020 (6) inc17 — antes copiaba los nodos sueltos. QA pidió «seleccionar y duplicar un
   * grupo de nodos, conservando la estructura y configuración correspondiente»: la estructura son
   * precisamente las aristas entre ellos, y sin ellas duplicar una rama de cinco pasos obligaba a
   * volver a cablearla a mano, que es el trabajo que la función pretendía ahorrar.
   *
   * Solo se replican las aristas cuyos DOS extremos están en el grupo. Una arista que sale hacia
   * un nodo de fuera no se copia: apuntaría al mismo destino que la original y crearía una
   * segunda entrada al resto del flujo que el usuario no ha pedido.
   *
   * Los TRIGGER se excluyen: un workflow tiene un único disparador.
   */
  duplicateNodes(nodeKeys: readonly string[]): WorkflowGraphNode[] {
    const keys = new Set(nodeKeys);
    const originals = this._nodes().filter((node) => keys.has(node.nodeKey));
    // TRIGGER es único: duplicarlo produciría dos disparadores.
    const duplicable = originals.filter((node) => node.nodeType !== 'TRIGGER');
    if (!duplicable.length) {
      return [];
    }
    this.pushHistory();
    const created: WorkflowGraphNode[] = [];
    // nodeKey original -> nodeKey de la copia, para reconstruir las aristas internas.
    const keyMap = new Map<string, string>();
    for (const original of duplicable) {
      const copy: WorkflowGraphNode = {
        ...original,
        id: null,
        nodeKey: generateNodeKey(original.nodeType, [
          ...this._nodes().map((n) => n.nodeKey),
          ...created.map((n) => n.nodeKey)
        ]),
        // Copia profunda: si no, ambos nodos compartirían el mismo objeto de config.
        config: structuredClone(original.config),
        positionX: original.positionX + 40,
        positionY: original.positionY + 40,
        validationStatus: 'PENDING'
      };
      keyMap.set(original.nodeKey, copy.nodeKey);
      created.push(copy);
    }
    this._nodes.update((nodes) => [...nodes, ...created]);

    const clonedEdges = this.cloneInternalEdges(keyMap);
    if (clonedEdges.length) {
      this._edges.update((edges) => [...edges, ...clonedEdges]);
    }

    const last = created[created.length - 1];
    if (last) {
      this._selection.set({ kind: 'node', nodeKey: last.nodeKey });
    }
    this.markDirty();
    return created;
  }

  /**
   * Aristas del grafo cuyos dos extremos están en `keyMap`, reapuntadas a las copias.
   * Compartida por duplicar y pegar: en ambos casos «la estructura» significa lo mismo.
   */
  private cloneInternalEdges(keyMap: Map<string, string>): WorkflowGraphEdge[] {
    return this._edges()
      .filter((edge) => keyMap.has(edge.sourceNodeKey) && keyMap.has(edge.targetNodeKey))
      .map((edge) => ({
        ...edge,
        sourceNodeKey: keyMap.get(edge.sourceNodeKey)!,
        targetNodeKey: keyMap.get(edge.targetNodeKey)!,
        config: structuredClone(edge.config)
      }));
  }

  // ── Portapapeles (INC_CRM_020 (4) inc14) ───────────────────────────────────
  //
  // QA pidió Ctrl+X sobre el lienzo. Cortar sin poder pegar no sirve de nada, así que el
  // portapapeles es el trío completo copiar/cortar/pegar. Vive en memoria y no en el
  // portapapeles del sistema: los nodos son objetos del grafo, no texto, y pegarlos en otra
  // aplicación no significa nada.

  /** Nodos copiados o cortados, ya clonados: el original puede borrarse después. */
  private clipboard: WorkflowGraphNode[] = [];
  /**
   * INC_CRM_020 (6) inc17 — aristas internas del recorte, ya clonadas. Se guardan aparte del
   * grafo porque `cutNodes` borra los originales inmediatamente: si se resolvieran al pegar, ya
   * no existirían y el recorte llegaría descableado.
   */
  private clipboardEdges: WorkflowGraphEdge[] = [];
  private readonly _canPaste = signal(false);
  readonly canPaste = this._canPaste.asReadonly();

  /**
   * Copia nodos al portapapeles, con las conexiones internas del grupo. TRIGGER se excluye igual
   * que en `duplicateNodes`: un workflow tiene un único disparador y pegar un segundo dejaría el
   * grafo inválido.
   */
  copyNodes(nodeKeys: readonly string[]): number {
    const keys = new Set(nodeKeys);
    const copiables = this._nodes()
      .filter((node) => keys.has(node.nodeKey) && node.nodeType !== 'TRIGGER')
      .map((node) => structuredClone(node));
    if (!copiables.length) {
      return 0;
    }
    const copiadas = new Set(copiables.map((node) => node.nodeKey));
    this.clipboard = copiables;
    this.clipboardEdges = this._edges()
      .filter((edge) => copiadas.has(edge.sourceNodeKey) && copiadas.has(edge.targetNodeKey))
      .map((edge) => structuredClone(edge));
    this._canPaste.set(true);
    return copiables.length;
  }

  /** Copia y borra. Devuelve cuántos nodos se cortaron (0 = nada elegible, no se toca el grafo). */
  cutNodes(nodeKeys: readonly string[]): number {
    const copiados = this.copyNodes(nodeKeys);
    if (!copiados) {
      return 0;
    }
    this.removeNodes(this.clipboard.map((node) => node.nodeKey));
    return copiados;
  }

  /**
   * Pega el contenido del portapapeles con claves nuevas y desplazado, para que no quede encima
   * del original. Se puede pegar varias veces: el portapapeles no se vacía.
   */
  pasteClipboard(): WorkflowGraphNode[] {
    if (!this.clipboard.length) {
      return [];
    }
    this.pushHistory();
    const created: WorkflowGraphNode[] = [];
    const keyMap = new Map<string, string>();
    for (const original of this.clipboard) {
      const copy: WorkflowGraphNode = {
        ...original,
        id: null,
        nodeKey: generateNodeKey(original.nodeType, [
          ...this._nodes().map((n) => n.nodeKey),
          ...created.map((n) => n.nodeKey)
        ]),
        config: structuredClone(original.config),
        positionX: original.positionX + 60,
        positionY: original.positionY + 60,
        validationStatus: 'PENDING'
      };
      keyMap.set(original.nodeKey, copy.nodeKey);
      created.push(copy);
    }
    this._nodes.update((nodes) => [...nodes, ...created]);

    // INC_CRM_020 (6) inc17 — se recablean las aristas guardadas al copiar, no las del grafo:
    // tras un Ctrl+X los originales ya no están.
    const pastedEdges = this.clipboardEdges
      .filter((edge) => keyMap.has(edge.sourceNodeKey) && keyMap.has(edge.targetNodeKey))
      .map((edge) => ({
        ...edge,
        sourceNodeKey: keyMap.get(edge.sourceNodeKey)!,
        targetNodeKey: keyMap.get(edge.targetNodeKey)!,
        config: structuredClone(edge.config)
      }));
    if (pastedEdges.length) {
      this._edges.update((edges) => [...edges, ...pastedEdges]);
    }

    const last = created[created.length - 1];
    if (last) {
      this._selection.set({ kind: 'node', nodeKey: last.nodeKey });
    }
    this.markDirty();
    return created;
  }

  /**
   * Crea una arista a partir de un arrastre puerto → nodo.
   * EL `branchKey` SALE DEL PUERTO DE ORIGEN: el usuario nunca lo elige (gotcha §6).
   * Devuelve false si la conexión no es válida, el puerto no existe o ya existe.
   */
  connect(sourceNodeKey: string, branchKey: string, targetNodeKey: string): boolean {
    const source = this._nodes().find((node) => node.nodeKey === sourceNodeKey);
    const target = this._nodes().find((node) => node.nodeKey === targetNodeKey);
    if (!source || !target || !this.isConnectionAllowed(source, target)) {
      return false;
    }
    const normalized = this.normalizeBranchKey(branchKey);
    const handles = getOutputBranchHandles(source);
    if (!handles.some((h) => h.branchKey === normalized)) {
      // El puerto no pertenece al nodo origen: el runtime no sabría resolver esa rama.
      return false;
    }
    const duplicate = this._edges().some(
      (e) => e.sourceNodeKey === sourceNodeKey && e.branchKey === normalized && e.targetNodeKey === targetNodeKey
    );
    if (duplicate) {
      return false;
    }

    this.pushHistory();
    const edge: WorkflowGraphEdge = {
      sourceNodeKey,
      targetNodeKey,
      branchKey: normalized,
      label: '',
      sortOrder: this._edges().length,
      config: {}
    };
    // Un puerto (branchKey) solo admite UNA salida: la nueva reemplaza a la anterior.
    this._edges.update((edges) => [
      ...edges.filter((e) => !(e.sourceNodeKey === sourceNodeKey && e.branchKey === normalized)),
      edge
    ]);
    this.markDirty();
    return true;
  }

  /** ¿Ese puerto ya tiene una salida? (la conexión nueva la reemplazaría). */
  branchHasConnection(sourceNodeKey: string, branchKey: string): boolean {
    const normalized = this.normalizeBranchKey(branchKey);
    return this._edges().some((e) => e.sourceNodeKey === sourceNodeKey && e.branchKey === normalized);
  }

  /** Reglas duras de conexión: sin self-loop y sin entrar a un TRIGGER (es el punto de entrada). */
  isConnectionAllowed(source: WorkflowGraphNode, target: WorkflowGraphNode): boolean {
    if (source.nodeKey === target.nodeKey) {
      return false;
    }
    if (target.nodeType === 'TRIGGER') {
      return false;
    }
    // END y ANNOTATION no tienen salidas.
    if (source.nodeType === 'END' || source.nodeType === 'ANNOTATION') {
      return false;
    }
    return true;
  }

  /** Actualiza la etiqueta de una arista. */
  updateEdge(edgeId: string, patch: Partial<Pick<WorkflowGraphEdge, 'label'>>): void {
    this.pushHistory(`edge:${edgeId}:${Object.keys(patch).join(',')}`);
    this._edges.update((edges) => edges.map((e) => (buildEdgeId(e) === edgeId ? { ...e, ...patch } : e)));
    this.markDirty();
  }

  findEdge(edgeId: string): WorkflowGraphEdge | undefined {
    return this._edges().find((e) => buildEdgeId(e) === edgeId);
  }

  /** Aristas que salen de un nodo. */
  outgoingOf(nodeKey: string): WorkflowGraphEdge[] {
    return this._edges().filter((e) => e.sourceNodeKey === nodeKey);
  }

  /** ¿Algún nodo apunta a este? */
  hasIncoming(nodeKey: string): boolean {
    return this._edges().some((e) => e.targetNodeKey === nodeKey);
  }

  setSelection(selection: WorkflowGraphSelection): void {
    this._selection.set(selection);
  }

  setSaveState(state: WorkflowSaveState): void {
    this._saveState.set(state);
  }

  setValidation(validation: WorkflowValidationResponse): void {
    this._validation.set(validation);
  }

  markSaved(): void {
    this._dirty.set(false);
    this._structuralDirty.set(false);
    this._saveState.set('saved');
  }

  /** ¿Queda algo que solo el PUT del grafo pueda persistir? */
  hasStructuralChanges(): boolean {
    return this._structuralDirty();
  }

  /**
   * El PATCH de posición de un nodo terminó bien. Si no queda ningún cambio estructural
   * pendiente, el grafo ya está persistido: el indicador puede decir "Guardado".
   */
  notePositionPersisted(): void {
    if (!this._structuralDirty()) {
      this._dirty.set(false);
      this._saveState.set('saved');
    }
  }

  private markDirty(structural = true): void {
    this._revision++;
    this._dirty.set(true);
    if (structural) {
      this._structuralDirty.set(true);
    }
    this._saveState.set('unsaved');
  }

  // ── Undo / redo ───────────────────────────────────────────────────────────

  private snapshot(): WorkflowGraphSnapshot {
    return {
      nodes: this._nodes().map((node) => ({ ...node, config: structuredClone(node.config) })),
      edges: this._edges().map((edge) => ({ ...edge, config: structuredClone(edge.config) }))
    };
  }

  /**
   * `editToken` identifica una edición continua (p. ej. teclear en un campo de un nodo). Dos
   * llamadas seguidas con el mismo token dentro de `HISTORY_COALESCE_MS` comparten el snapshot
   * inicial: deshacer devuelve el campo a como estaba ANTES de la ráfaga, no una letra atrás.
   * Sin token (acciones discretas: crear, borrar, conectar…) siempre se apila.
   */
  private pushHistory(editToken = ''): void {
    const now = Date.now();
    const coalesce = editToken !== '' && editToken === this.lastEditToken && now - this.lastEditAt < HISTORY_COALESCE_MS;
    this.lastEditToken = editToken;
    this.lastEditAt = now;
    if (coalesce) {
      // Cualquier acción nueva invalida la rama de rehacer, también dentro de la ráfaga.
      this.redoStack = [];
      this.syncHistoryFlags();
      return;
    }
    this.undoStack.push(this.snapshot());
    if (this.undoStack.length > MAX_HISTORY) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.syncHistoryFlags();
  }

  undo(): void {
    const previous = this.undoStack.pop();
    if (!previous) {
      return;
    }
    this.redoStack.push(this.snapshot());
    this.applySnapshot(previous);
  }

  redo(): void {
    const next = this.redoStack.pop();
    if (!next) {
      return;
    }
    this.undoStack.push(this.snapshot());
    this.applySnapshot(next);
  }

  private applySnapshot(snapshot: WorkflowGraphSnapshot): void {
    this._nodes.set(snapshot.nodes);
    this._edges.set(snapshot.edges);
    const selection = this._selection();
    if (selection?.kind === 'node' && !snapshot.nodes.some((node) => node.nodeKey === selection.nodeKey)) {
      this._selection.set(null);
    }
    if (selection?.kind === 'edge' && !snapshot.edges.some((edge) => buildEdgeId(edge) === selection.edgeId)) {
      this._selection.set(null);
    }
    this.markDirty();
    this.syncHistoryFlags();
  }

  private syncHistoryFlags(): void {
    this._canUndo.set(this.undoStack.length > 0);
    this._canRedo.set(this.redoStack.length > 0);
  }

  // ── Serialización ─────────────────────────────────────────────────────────

  /**
   * Payload de PUT /builder/graph. Es REPLACE: el store debe ser la verdad completa del
   * grafo antes de enviar, porque lo que no viaje aquí se borra en el backend
   * (`saveGraph` hace `deleteByWorkflowVersion` de las aristas y borra los nodos ausentes).
   *
   * Save ATÓMICO por nodeKey (contrato acordado con backend): cada arista viaja SIEMPRE
   * con `sourceNodeKey`/`targetNodeKey` — la identidad estable del grafo — de modo que una
   * arista que cuelga de un nodo recién creado (id=null) YA es expresable en el batch (el
   * backend resuelve el id definitivo tras crear el nodo, todo en un único PUT). Cuando el
   * nodo ya tiene id de BD se envía ADEMÁS `sourceNodeId`/`targetNodeId` (retrocompatible).
   * Cargar y guardar sin tocar nada sigue siendo identidad para todo grafo ya persistido.
   */
  toSaveRequest(): WorkflowGraphSaveRequest {
    const versionId = this._versionId();
    if (versionId == null) {
      throw new Error('No hay versión editable cargada en el store.');
    }
    const idByKey = new Map<string, number>();
    for (const node of this._nodes()) {
      if (node.id !== null) {
        idByKey.set(node.nodeKey, node.id);
      }
    }

    const nodes: WorkflowNodeRequest[] = this._nodes().map((node) => ({
      id: node.id ?? undefined,
      workflowVersionId: versionId,
      nodeKey: node.nodeKey,
      nodeType: node.nodeType,
      nodeSubtype: node.nodeSubtype,
      title: node.title.trim() || null,
      description: node.description?.trim() || null,
      positionX: node.positionX,
      positionY: node.positionY,
      config: node.config ?? {},
      isActive: node.isActive
    }));

    const edges: WorkflowEdgeRequest[] = this._edges().map((edge, index) => {
      const sourceNodeId = idByKey.get(edge.sourceNodeKey);
      const targetNodeId = idByKey.get(edge.targetNodeKey);
      // Siempre por nodeKey (save atómico); el id numérico se suma solo si el nodo ya existe.
      return {
        workflowVersionId: versionId,
        sourceNodeKey: edge.sourceNodeKey,
        targetNodeKey: edge.targetNodeKey,
        ...(sourceNodeId !== undefined ? { sourceNodeId } : {}),
        ...(targetNodeId !== undefined ? { targetNodeId } : {}),
        branchKey: edge.branchKey,
        label: edge.label.trim() || null,
        sortOrder: index,
        config: edge.config ?? {}
      };
    });

    return { workflowVersionId: versionId, nodes, edges };
  }

  /** Nodo por clave. */
  findNode(nodeKey: string): WorkflowGraphNode | undefined {
    return this._nodes().find((node) => node.nodeKey === nodeKey);
  }
}
